import { Box, Button, Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { highestSeverity, severityColor } from '@/lib/alert-severity';
import { getResidentAlerts, type ResidentAlert } from '@/lib/api/resident-alerts';

interface Props {
  residentId: string;
  /** 근거 확인용 탭으로 이동 — 프로그램에 맞는 탭을 연다 */
  onOpenTab: (tab: 'period' | 'voice') => void;
}

function formatDate(ymd: string): string {
  const [y, m, d] = ymd.split('-');
  return `${y}. ${Number(m)}. ${Number(d)}.`;
}

/** 경보 한 건의 근거 문장 — 왜 이 경보가 떴는지 */
function reasonText(a: ResidentAlert): string {
  if (a.drop) {
    return `어제 ${a.drop.todayAvgScore}점 ← 그제 ${a.drop.yesterdayAvgScore}점 (${a.drop.dropPct}%)`;
  }
  if (a.usedDays === null || a.windowDays === null) {
    // 조회 자체를 못한 경우. '0일 사용'으로 단정하면 연동 장애를 미사용으로 오해한다.
    return '사용 이력을 불러오지 못했습니다';
  }
  const last = a.lastUsedAt ? `마지막 사용 ${formatDate(a.lastUsedAt)}` : '최근 사용 기록 없음';
  return `${last} · 최근 ${a.windowDays}일 중 ${a.usedDays}일 사용`;
}

/**
 * 상세 패널 경보 근거 카드.
 *
 * 목록·배너의 경보는 '30일 미사용' 한 줄이라, 클릭해 들어와도 어떤 프로그램을
 * 언제부터 안 썼는지 알 수 없었다. 조치로 이어지려면 근거가 같이 보여야 한다.
 * 활성 경보가 없으면 아무것도 그리지 않는다.
 */
export function AlertReasonCard({ residentId, onOpenTab }: Props) {
  const { data } = useQuery({
    queryKey: ['residents', 'alerts', residentId],
    queryFn: () => getResidentAlerts(residentId),
    staleTime: 30 * 1000,
  });

  const alerts = data ?? [];
  if (alerts.length === 0) return null;

  // 강조선은 실제로 가장 높은 심각도를 따른다. 늘 빨강으로 두면 일반 경보(급락 등)만
  // 있는 입소자도 긴급으로 읽혀, 정작 급한 대상과 구분되지 않는다.
  const accent = severityColor(highestSeverity(alerts.map((a) => a.severity))).dot;

  return (
    <Card variant="outlined" sx={{ borderLeft: `4px solid ${accent}` }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack divider={<Divider flexItem sx={{ my: 1 }} />}>
          {alerts.map((a) => (
            <Stack key={a.type} direction="row" alignItems="center" spacing={1.5}>
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: severityColor(a.severity).dot,
                  flexShrink: 0,
                }}
              />
              <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700}>
                  {a.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {reasonText(a)}
                </Typography>
              </Stack>
              <Button
                size="small"
                onClick={() => onOpenTab(a.type === 'VOICE_UNUSED' ? 'voice' : 'period')}
                sx={{ flexShrink: 0 }}
              >
                기록 보기
              </Button>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
